import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import { CheckmarkIcon } from '../../../components/svgs/icons/CheckmarkIcon';
import { Button } from '../../Button';
import { THEME } from '../../Theme/theme';

const DEFAULT_CONFIRM_LABEL = 'Looks good';
const DEFAULT_DECLINE_LABEL = 'Make changes';

export interface ConfirmationResponsePayload {
	confirmed: boolean;
	notes?: string;
}

export function getLatestConfirmationToolCallId(messages: Api.AIChatMessage[] | undefined) {
	if (!messages) {
		return undefined;
	}
	for (let i = messages.length - 1; i >= 0; i--) {
		const contents = messages[i].contents;
		if (!contents) {
			continue;
		}
		for (let j = contents.length - 1; j >= 0; j--) {
			const content = contents[j];
			if (content._type !== 'AIToolContent') {
				continue;
			}
			const toolCall = (content as Api.AIToolContent).toolCall;
			if (toolCall?.toolName === 'lev-confirmation') {
				return toolCall.id;
			}
		}
	}
	return undefined;
}

function parseAnsweredState(state: string | undefined): { confirmed?: boolean } | undefined {
	if (!state) {
		return undefined;
	}
	try {
		const parsed = JSON.parse(state);
		return { confirmed: typeof parsed?.confirmed === 'boolean' ? parsed.confirmed : undefined };
	} catch {
		return {};
	}
}

interface ConfirmationCardProps {
	toolCall: Api.ConfirmationToolCall;
	canRespond: boolean;
	isSuperseded?: boolean;
	onRespond?: (response: ConfirmationResponsePayload) => void;
}

export function ConfirmationCard({ toolCall, canRespond, isSuperseded, onRespond }: ConfirmationCardProps) {
	const answeredResponse = parseAnsweredState(toolCall.state);
	const confirmLabel = toolCall.confirmLabel || DEFAULT_CONFIRM_LABEL;
	const declineLabel = toolCall.declineLabel || DEFAULT_DECLINE_LABEL;
	const answeredLabel =
		answeredResponse?.confirmed === true
			? confirmLabel
			: answeredResponse?.confirmed === false
				? declineLabel
				: 'Responded';
	return (
		<div className={css(s.card, isSuperseded && !answeredResponse ? s.superseded : null)}>
			{toolCall.title ? <div className={css(s.title)}>{toolCall.title}</div> : null}
			{toolCall.summary ? <div className={css(s.summary)}>{toolCall.summary}</div> : null}
			{toolCall.items?.length ? (
				<div className={css(s.items)}>
					{toolCall.items.map((item, i) => (
						<div key={`${toolCall.id}-item-${i}`}>
							<div className={css(s.itemLabel)}>{item.label}</div>
							{item.detail ? <div className={css(s.itemDetail)}>{item.detail}</div> : null}
						</div>
					))}
				</div>
			) : null}
			{answeredResponse ? (
				<div className={css(s.answeredRow)}>
					{answeredResponse.confirmed === true ? <CheckmarkIcon fillColor={THEME.colors.success.main} /> : null}
					<span>{answeredLabel}</span>
				</div>
			) : (
				<>
					<div className={css(s.buttonRow)}>
						<Button disabled={!canRespond} onClick={() => onRespond?.({ confirmed: true })}>
							{confirmLabel}
						</Button>
						<Button variant='secondary' disabled={!canRespond} onClick={() => onRespond?.({ confirmed: false })}>
							{declineLabel}
						</Button>
					</div>
					{isSuperseded && !answeredResponse ? (
						<div className={css(s.supersededHint)}>A newer confirmation replaced this</div>
					) : null}
				</>
			)}
		</div>
	);
}

const s = StyleSheet.create({
	card: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
		padding: THEME.spacing.md,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		backgroundColor: THEME.colors.background.main,
		fontWeight: THEME.typography.fontWeights.standard,
	},
	superseded: {
		opacity: 0.6,
	},
	title: {
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
	},
	summary: {
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.main,
		lineHeight: 1.5,
	},
	items: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
	},
	itemLabel: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
		lineHeight: 1.5,
	},
	itemDetail: {
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.main,
		lineHeight: 1.5,
	},
	buttonRow: {
		display: 'flex',
		gap: THEME.spacing.sm,
	},
	answeredRow: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.xs,
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.light,
	},
	supersededHint: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
});
